import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3, Settings, 
  LogOut, Menu, X, User as UserIcon, TrendingUp, TrendingDown, CalendarDays, Sun, Moon,
  ChevronLeft, ChevronRight, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import { BrandLogo } from './BrandLogo';

interface SidebarNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isCollapsed: boolean;
  onClick?: () => void;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ to, icon, label, isCollapsed, onClick }) => {
  return (
    <NavLink 
      to={to} 
      onClick={onClick}
      title={isCollapsed ? label : undefined}
      className={({ isActive }) => 
        `flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-3.5'} py-3 rounded-2xl transition-all duration-200 font-bold text-xs tracking-wider uppercase group relative ${
          isActive 
            ? 'bg-gradient-to-r from-white/25 to-white/15 dark:from-purple-600/35 dark:to-purple-600/20 text-white shadow-lg shadow-purple-950/20 backdrop-blur-md border border-white/20 dark:border-purple-400/30' 
            : 'text-purple-200/80 dark:text-slate-400 hover:bg-white/10 dark:hover:bg-slate-800/60 hover:text-white dark:hover:text-slate-100'
        }`
      }
    >
      <span className="shrink-0 transition-transform duration-200 group-hover:scale-110">{icon}</span>
      {!isCollapsed && (
        <span className="ml-3 truncate font-semibold tracking-wide text-xs">{label}</span>
      )}
      {/* Tooltip on collapsed hover */}
      {isCollapsed && (
        <span className="absolute left-full ml-3 px-2.5 py-1 bg-slate-900 text-white text-[11px] font-semibold rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 border border-slate-700">
          {label}
        </span>
      )}
    </NavLink>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Sidebar Collapsed state (saved to localStorage)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const toggleSidebar = () => {
    const nextState = !isSidebarCollapsed;
    setIsSidebarCollapsed(nextState);
    localStorage.setItem('sidebar_collapsed', String(nextState));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pageTitle = () => {
    switch (location.pathname) {
      case '/dashboard': return 'Despesas & Lançamentos';
      case '/revenues': return 'Receitas & Entradas';
      case '/agenda': return 'Agenda & Calendário Financeiro';
      case '/reports': return 'Relatórios Financeiros';
      case '/settings': return 'Configurações do Sistema';
      case '/profile': return 'Meu Perfil';
      case '/users': return 'Gestão de Usuários';
      case '/quotes': return 'Cotações & Orçamentos';
      default: return 'Painel Financeiro';
    }
  };

  return (
    <div className="flex h-screen print:h-auto bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 overflow-hidden print:overflow-visible font-sans transition-colors duration-300">
      
      {/* =========================================================================
          Desktop Left Sidebar (Collapsible with smooth animation)
         ========================================================================= */}
      <aside 
        className={`hidden md:flex flex-col h-full bg-gradient-to-b from-[#2e0854] via-[#3b0764] to-[#1e1b4b] dark:from-[#0b0f19] dark:via-[#111827] dark:to-[#030712] border-r border-purple-800/40 dark:border-slate-800 shadow-2xl z-30 transition-[width] duration-300 ease-in-out select-none print:hidden shrink-0 ${
          isSidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Sidebar Header: Logo & Collapse Button */}
        <div className={`h-20 flex items-center ${isSidebarCollapsed ? 'justify-center px-2 flex-col gap-2' : 'justify-between px-4 gap-2'} border-b border-purple-700/30 dark:border-slate-800 shrink-0 relative`}>
          <div className="flex items-center overflow-hidden min-w-0 flex-1">
            <BrandLogo 
              size="md" 
              linkTo="/dashboard" 
              theme="glass" 
              iconOnly={isSidebarCollapsed} 
              showSubtitle={!isSidebarCollapsed}
            />
          </div>

          <button
            onClick={toggleSidebar}
            className={`p-1.5 shrink-0 text-purple-200/80 hover:text-white hover:bg-white/10 dark:hover:bg-slate-800/60 rounded-xl transition-all active:scale-95 ${
              isSidebarCollapsed ? 'mt-1' : ''
            }`}
            title={isSidebarCollapsed ? "Expandir Menu" : "Recolher Menu"}
            aria-label={isSidebarCollapsed ? "Expandir Menu" : "Recolher Menu"}
          >
            {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-5 px-3 space-y-1.5 overflow-y-auto overflow-x-hidden">
          {!isSidebarCollapsed && (
            <p className="px-3 text-[10px] font-extrabold text-purple-300/60 dark:text-slate-500 uppercase tracking-widest mb-2">
              Principal
            </p>
          )}

          <SidebarNavItem 
            to="/dashboard" 
            icon={<TrendingDown size={19} />} 
            label="Despesas" 
            isCollapsed={isSidebarCollapsed} 
          />
          <SidebarNavItem 
            to="/revenues" 
            icon={<TrendingUp size={19} />} 
            label="Receitas" 
            isCollapsed={isSidebarCollapsed} 
          />
          <SidebarNavItem 
            to="/agenda" 
            icon={<CalendarDays size={19} />} 
            label="Agenda" 
            isCollapsed={isSidebarCollapsed} 
          />
          <SidebarNavItem 
            to="/reports" 
            icon={<BarChart3 size={19} />} 
            label="Relatórios" 
            isCollapsed={isSidebarCollapsed} 
          />

          <div className="pt-4 mt-4 border-t border-purple-700/20 dark:border-slate-800">
            {!isSidebarCollapsed && (
              <p className="px-3 text-[10px] font-extrabold text-purple-300/60 dark:text-slate-500 uppercase tracking-widest mb-2">
                Sistema
              </p>
            )}
            <SidebarNavItem 
              to="/settings" 
              icon={<Settings size={19} />} 
              label="Configurações" 
              isCollapsed={isSidebarCollapsed} 
            />
          </div>
        </nav>

        {/* Sidebar Footer: User & System Controls */}
        <div className="p-3 border-t border-purple-700/30 dark:border-slate-800 bg-black/10 dark:bg-black/20 shrink-0 space-y-2">
          {/* User Profile Tile */}
          <NavLink
            to="/profile"
            title={isSidebarCollapsed ? (user?.name || "Meu Perfil") : undefined}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2' : 'p-2.5 gap-3'} rounded-2xl hover:bg-white/10 dark:hover:bg-slate-800 transition-all group`}
          >
            <div className="w-9 h-9 rounded-full bg-purple-900 dark:bg-slate-800 flex items-center justify-center overflow-hidden border-2 border-white/30 dark:border-slate-700 shadow-sm shrink-0 group-hover:scale-105 transition-transform">
              {user?.photoUrl ? (
                <img src={user.photoUrl} alt="User" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="text-white dark:text-slate-300" size={17} />
              )}
            </div>
            {!isSidebarCollapsed && (
              <div className="overflow-hidden text-left flex-1">
                <p className="text-white font-bold text-xs truncate leading-tight">{user?.name}</p>
                <span className="text-[10px] text-purple-300/80 dark:text-slate-400 font-medium uppercase tracking-wider block">
                  {user?.role || 'Admin'}
                </span>
              </div>
            )}
          </NavLink>

          {/* Quick Controls: Dark Mode & Logout */}
          <div className={`flex items-center ${isSidebarCollapsed ? 'flex-col gap-2' : 'justify-between px-2 pt-1'}`}>
            <button
              onClick={toggleDarkMode}
              className="p-2 text-purple-200/80 hover:text-white hover:bg-white/10 dark:hover:bg-slate-800 rounded-xl transition-all"
              title={isDarkMode ? "Modo Claro" : "Modo Escuro"}
              aria-label="Alternar Tema"
            >
              {isDarkMode ? <Sun size={17} className="text-amber-300" /> : <Moon size={17} />}
            </button>

            <button
              onClick={handleLogout}
              className="p-2 text-purple-200/80 hover:text-red-300 dark:hover:text-red-400 hover:bg-white/10 dark:hover:bg-slate-800 rounded-xl transition-all"
              title="Sair do Sistema"
              aria-label="Sair"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>

      {/* =========================================================================
          Main Content Container
         ========================================================================= */}
      <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
        
        {/* Top Header Bar */}
        <header className="h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800 flex items-center justify-between px-4 sm:px-6 z-20 shrink-0 print:hidden transition-colors">
          <div className="flex items-center gap-3">
            {/* Mobile Menu Trigger */}
            <button 
              onClick={() => setIsMobileMenuOpen(true)} 
              className="md:hidden p-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all active:scale-95"
              aria-label="Abrir Menu"
            >
              <Menu size={22} />
            </button>

            {/* Mobile Brand Logo */}
            <div className="md:hidden">
              <BrandLogo size="sm" linkTo="/dashboard" theme="light" />
            </div>

            {/* Desktop Page Title & Breadcrumb */}
            <div className="hidden md:block">
              <h1 className="text-base font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                {pageTitle()}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Quick Dark Mode Toggle on Header (Mobile & Desktop) */}
            <button 
              onClick={toggleDarkMode} 
              className="p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
              title={isDarkMode ? "Modo claro" : "Modo escuro"}
              aria-label="Alternar Tema"
            >
              {isDarkMode ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} />}
            </button>

            <NavLink 
              to="/profile" 
              className="flex items-center gap-2 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
              title="Meu Perfil"
            >
              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center justify-center font-bold text-xs overflow-hidden border border-purple-200 dark:border-purple-700">
                {user?.photoUrl ? (
                  <img src={user.photoUrl} alt="User" className="w-full h-full object-cover" />
                ) : (
                  <span>{user?.name ? user.name.slice(0, 2).toUpperCase() : 'US'}</span>
                )}
              </div>
              <span className="hidden sm:inline font-bold text-xs text-slate-700 dark:text-slate-300 max-w-[120px] truncate">
                {user?.name}
              </span>
            </NavLink>
          </div>
        </header>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex animate-fade-in">
            <div className="w-72 bg-gradient-to-b from-purple-950 via-purple-900 to-indigo-950 dark:from-slate-900 dark:to-slate-950 backdrop-blur-2xl h-full shadow-2xl flex flex-col border-r border-purple-800/50 dark:border-slate-800 animate-slide-up">
              <div className="h-16 flex items-center justify-between px-5 border-b border-white/10 dark:border-slate-800">
                <BrandLogo size="sm" linkTo="/dashboard" theme="glass" onClick={() => setIsMobileMenuOpen(false)} />
                <button 
                  onClick={() => setIsMobileMenuOpen(false)} 
                  className="p-1.5 text-purple-200 dark:text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Fechar Menu"
                >
                  <X size={22}/>
                </button>
              </div>

              <div className="flex-1 py-4 px-3 space-y-2 overflow-y-auto">
                <p className="px-4 text-[10px] font-extrabold text-purple-300/80 dark:text-slate-500 uppercase tracking-widest mb-2">Financeiro</p>
                <SidebarNavItem to="/dashboard" icon={<TrendingDown size={18} />} label="Despesas" isCollapsed={false} onClick={() => setIsMobileMenuOpen(false)} />
                <SidebarNavItem to="/revenues" icon={<TrendingUp size={18} />} label="Receitas" isCollapsed={false} onClick={() => setIsMobileMenuOpen(false)} />
                <SidebarNavItem to="/agenda" icon={<CalendarDays size={18} />} label="Agenda" isCollapsed={false} onClick={() => setIsMobileMenuOpen(false)} />
                <SidebarNavItem to="/reports" icon={<BarChart3 size={18} />} label="Relatórios" isCollapsed={false} onClick={() => setIsMobileMenuOpen(false)} />

                <div className="pt-3">
                  <p className="px-4 text-[10px] font-extrabold text-purple-300/80 dark:text-slate-500 uppercase tracking-widest mb-2">Administração</p>
                  <SidebarNavItem to="/settings" icon={<Settings size={18} />} label="Configurações" isCollapsed={false} onClick={() => setIsMobileMenuOpen(false)} />
                  <SidebarNavItem to="/profile" icon={<UserIcon size={18} />} label="Meu Perfil" isCollapsed={false} onClick={() => setIsMobileMenuOpen(false)} />
                </div>
              </div>

              <div className="p-4 border-t border-white/10 dark:border-slate-800">
                <button 
                  onClick={handleLogout} 
                  className="flex items-center justify-center gap-2 text-white bg-red-600/90 hover:bg-red-600 text-sm font-bold w-full py-3 rounded-xl shadow-lg transition-all active:scale-95"
                >
                  <LogOut size={16} /> Sair do Sistema
                </button>
              </div>
            </div>
            <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
          </div>
        )}

        {/* Scrollable Main Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden print:overflow-visible p-4 sm:p-6 lg:p-8 relative flex flex-col bg-slate-50 dark:bg-slate-950">
          {/* Page Transition Wrapper */}
          <div key={location.pathname} className="max-w-7xl w-full mx-auto flex-1 flex flex-col relative z-10 animate-fade-in">
            {children}
          </div>
          
          {/* Required System Footer */}
          <footer className="mt-auto pt-8 pb-4 text-center text-xs text-slate-500 dark:text-slate-400 border-t border-slate-200/80 dark:border-slate-800/80 transition-colors print:hidden">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 font-medium">
              <span>2026 Gestão Fácil ERP ® • Todos os direitos reservados.</span>
              <span className="hidden sm:inline text-slate-300 dark:text-slate-600">•</span>
              <span>
                Desenvolvido por{" "}
                <a 
                  href="http://alonedev.com/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="font-bold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:underline transition-colors"
                >
                  AloneDev.com ®
                </a>{" "}
                • Todos os direitos reservados.
              </span>
            </div>
          </footer>
          
          {/* Background Ambient Glows */}
          <div className="fixed top-20 left-10 w-80 h-80 bg-purple-500/5 dark:bg-purple-600/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
          <div className="fixed bottom-20 right-10 w-96 h-96 bg-indigo-500/5 dark:bg-indigo-600/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
        </main>
      </div>
    </div>
  );
};
